import type { CSSProperties } from 'react';
import { assetUrl, IMG } from '@/lib/assets';
import { configuredOr } from '@/lib/configValue';
import { fontStyleToCss, type FontOptions } from '@/lib/fontStyle';
import { formatTravelTime, PinDirections } from '@/lib/pin';

/**
 * A map pin: a rounded label box with an optional icon, a title and a travel time, plus a
 * CSS-triangle arrow on one of its four sides. Port of legacy `Pin.vue`.
 *
 * **Not a registered widget.** It is a child of `TravelWidget` (SYM-501) and is built here
 * because it is a shared presentational primitive like the text blocks
 * (docs/MIGRATION-WIDGETS.md §1). No deployed config uses `TravelWidget`, so nothing on a
 * wall renders this today.
 *
 * ## Two legacy bugs fixed here
 *
 * - **W-3** — `:src="iconPath"` and `:class="getOrientationClass"` bound the *functions*
 *   rather than their results, so the icon has never rendered and the label box has never
 *   received its orientation class. Both are called here.
 * - **W-4** — the icon path appended `-icon.svg` to the configured name while the files on
 *   disk (`wind.svg`, `people.svg`) carry no such suffix, so even a correctly bound `src`
 *   would have missed. The exact name is tried first, then the suffixed one.
 *
 * The two compound: fixing either alone changes nothing. Together they mean a pin given an
 * `icon` now renders one. Legacy `travelWidget.vue` never passed `icon` to this component at
 * all, so this is only visible once SYM-501 wires `pin.icon` through — flagged for SYM-506
 * either way, because it is a deliberate visual change rather than a regression.
 */

/** Percent offsets within the map container. */
export type PinPosition = {
  top?: number;
  left?: number;
};

/** `options.pinStyle` from a `TravelWidget` config, shared by every pin it renders. */
export type PinStyle = {
  /** Background of the label box *and* the colour of the arrow. Default `green`. */
  color?: string;
  font?: FontOptions;
};

export type PinProps = {
  /** No title renders the arrow alone — legacy `v-if="title !== undefined"`. */
  title?: string;
  /** Travel time in minutes. `0` or absent renders no second line. */
  subtitle?: number;
  /** Resolved from `img/icons/`, with and without the legacy `-icon` suffix (W-4). */
  icon?: string;
  /**
   * One of `PinDirections`, but typed `string` because it arrives from `pin.arrow` in an
   * unvalidated config. Anything unrecognised falls back to `DOWN`, exactly as legacy did —
   * a typo in a pin costs an arrow direction, not a widget.
   */
  pinDirection?: string;
  pinStyle?: PinStyle;
  position?: PinPosition;
};

export function Pin({ title, subtitle, icon, pinDirection, pinStyle, position }: PinProps) {
  const color = configuredOr(pinStyle?.color, 'green');
  const travelTime = formatTravelTime(subtitle);
  const iconUrl = icon ? assetUrl(`${IMG.icons}${icon}.svg`, `${IMG.icons}${icon}-icon.svg`) : undefined;

  /*
   * Legacy `'0.8 rem'` — with a space — is invalid CSS, so the declaration was dropped and
   * the pin text inherited its size. `0.8rem` is what was intended and what
   * docs/MIGRATION-WIDGETS.md §2 specifies; it only applies when a config supplies no
   * `pinStyle.font.fontSize`, and none does today.
   */
  const fontStyle = fontStyleToCss(pinStyle?.font, { fontSize: '0.8rem' });

  return (
    <div
      className="pin text-white flex flex-col items-center"
      style={{
        position: 'absolute',
        top: position?.top ? `${String(position.top)}%` : '0',
        left: position?.left ? `${String(position.left)}%` : '0',
      }}
    >
      <div className={orientationClass(pinDirection)}>
        {title !== undefined && (
          // W-3: the orientation class belongs here too, and it is what puts the icon above
          // the text for an `UP`/`DOWN` pin and beside it for `LEFT`/`RIGHT`.
          <div
            className={`rounded-lg px-2 py-2 pin-container ${orientationClass(pinDirection)}`}
            style={{ background: color }}
          >
            {iconUrl !== undefined && (
              <img className="object-contain object-center h-5 w-5" src={iconUrl} alt={icon} />
            )}
            {/*
             * The responsive text utilities are legacy, kept for DOM parity. `text-2xs` is not
             * a Tailwind class in either version; the others do apply and set both a font size
             * and a *unitless* line height.
             *
             * The font style goes on each `<p>`, not on this wrapper, and that placement is
             * load-bearing rather than tidy: legacy styles `.pin-container p`, so the wrapper
             * keeps `text-base`'s own font size and the `<p>` inherits its resolved
             * `line-height` as a length. Hoisting the declaration one level up changes the
             * font size the unitless line height multiplies, which is a different line box —
             * measured at 1920px on `__travel-test`, `26.4px` against the legacy `33px`, and a
             * pin label box 6.6px shorter per line. Found by SYM-506's DOM diff.
             */}
            <div className="text-center text-2xs lg:text-xs xl:text-sm 2xl:text-base">
              <p style={fontStyle}>{title}</p>
              {travelTime !== '' && (
                <p className="pt-1" style={fontStyle}>
                  {travelTime}
                </p>
              )}
            </div>
          </div>
        )}
        <div style={arrowStyle(pinDirection, color)} />
      </div>
    </div>
  );
}

/** Which way the label box and arrow stack. Legacy `getOrientationClass()`, verbatim. */
function orientationClass(direction: string | undefined): string {
  if (direction === PinDirections.Up) return 'flex flex-col-reverse justify-center items-center';
  if (direction === PinDirections.Right) return 'flex flex-row justify-center items-center';
  if (direction === PinDirections.Left) return 'flex flex-row-reverse justify-center items-center';
  return 'flex flex-col justify-center items-center';
}

/**
 * The CSS-triangle arrow: a zero-sized box with three borders, one solid and two transparent.
 *
 * Inline rather than four classes in `global.css`, because the solid border takes its colour
 * from `color` — the legacy rules set `color: v-bind(color)` and let the border inherit
 * `currentColor` — and that value is per-pin config. Four static classes plus one dynamic
 * declaration is more moving parts than one style object.
 *
 * Note the side is the *opposite* of the direction: a pin pointing `DOWN` has its solid
 * border on top, because the triangle hangs below the label box.
 */
function arrowStyle(direction: string | undefined, color: string): CSSProperties {
  const base: CSSProperties = { width: 0, height: 0, color };

  const solid = '0.35rem solid';
  const clear = '0.35rem solid transparent';
  const horizontal = { borderLeft: clear, borderRight: clear };
  const vertical = { borderTop: clear, borderBottom: clear };

  if (direction === PinDirections.Up) return { ...base, ...horizontal, borderBottom: solid };
  if (direction === PinDirections.Right) return { ...base, ...vertical, borderLeft: solid };
  if (direction === PinDirections.Left) return { ...base, ...vertical, borderRight: solid };
  return { ...base, ...horizontal, borderTop: solid };
}
